import React, { useMemo, useCallback } from "react";
import { Box, IconButton, TextField, Tooltip, Typography, Checkbox, Select, MenuItem, ListItemText } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { debounce } from "lodash";
import { StripedDataGrid } from "components/StripedDataGrid";
import {
  IProdutoListagemN1,
  IFiltrosListagemN1,
  IOrdenacaoListagemN1,
  IPaginacaoListagemN1,
  OPCOES_STATUS,
  OPCOES_SITUACAO,
} from "../types/materiaisN1.types";
import { ImageThumbnail } from "./ImageThumbnail";

interface GridMateriaisProps {
  produtos: IProdutoListagemN1[];
  isLoading: boolean;
  filtros: IFiltrosListagemN1;
  ordenacao: IOrdenacaoListagemN1;
  paginacao: IPaginacaoListagemN1;
  onFiltrosChange: (filtros: IFiltrosListagemN1) => void;
  onOrdenacaoChange: (ordenacao: IOrdenacaoListagemN1) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (limit: number) => void;
  onVisualizarDetalhes: (id: number) => void;
}

export const GridMateriais: React.FC<GridMateriaisProps> = ({
  produtos,
  isLoading,
  filtros,
  ordenacao,
  paginacao,
  onFiltrosChange,
  onOrdenacaoChange,
  onPageChange,
  onPageSizeChange,
  onVisualizarDetalhes,
}) => {
  // Debounce para filtros de texto (300ms)
  const debouncedFiltroChange = useMemo(
    () =>
      debounce((campo: keyof IFiltrosListagemN1, valor: string) => {
        onFiltrosChange({
          ...filtros,
          [campo]: valor || undefined,
        });
      }, 300),
    [filtros, onFiltrosChange]
  );

  // Handler: Limpar todos os filtros
  const handleLimparFiltros = useCallback(() => {
    onFiltrosChange({});
  }, [onFiltrosChange]);

  // FASE 05 — 2ª validação/PO 2026-07-24 (C3): filtros de Status e Situação passam a ser
  // multi-select. Enviam o código exato do enum (array), que o backend já reconhece —
  // antes era texto livre e nenhum valor batia com o enum.
  const opcoesPorCampo: Record<string, ReadonlyArray<{ value: string; label: string }>> = {
    status: OPCOES_STATUS,
    situacao: OPCOES_SITUACAO,
  };

  const handleFiltroMultiSelect = useCallback(
    (campo: "status" | "situacao", valores: string[]) => {
      onFiltrosChange({
        ...filtros,
        [campo]: valores.length > 0 ? valores : undefined,
      });
    },
    [filtros, onFiltrosChange]
  );

  // Colunas do grid (simplificadas, sem header customizado)
  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [];

    // Coluna: Imagem
    cols.push({
      field: "imagem",
      headerName: "Imagem",
      width: 80,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) => (
        <ImageThumbnail
          urlImagem={params.row.urlImagem}
          temImagem={params.row.temImagem}
          alt={params.row.descricao}
        />
      ),
    });

    // Coluna: Código
    // FASE 05 — 2ª validação/PO 2026-07-24 (C2): apresentar o código por completo,
    // com tooltip para códigos longos (mesmo padrão da coluna Descrição).
    cols.push({
      field: "codigo",
      headerName: "Código",
      width: 160,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    });

    // Coluna: Descrição
    cols.push({
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    });

    // Coluna: Referência
    cols.push({
      field: "referencia",
      headerName: "Referência",
      width: 120,
      sortable: true,
    });

    // Coluna: Linha
    cols.push({
      field: "linha",
      headerName: "Linha",
      width: 120,
      sortable: true,
    });

    // Coluna: Status
    cols.push({
      field: "status",
      headerName: "Status",
      width: 110,
      sortable: true,
      renderCell: (params) => {
        const statusLabel = OPCOES_STATUS.find((s) => s.value === params.value)?.label || params.value;
        return <Typography variant="body2">{statusLabel}</Typography>;
      },
    });

    // Coluna: Situação
    cols.push({
      field: "situacao",
      headerName: "Situação",
      width: 120,
      sortable: true,
      renderCell: (params) => {
        const situacaoLabel = OPCOES_SITUACAO.find((s) => s.value === params.value)?.label || params.value;
        return <Typography variant="body2">{situacaoLabel}</Typography>;
      },
    });

    // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 7/P1): coluna Origem REMOVIDA da exibição.
    // O campo `origem` continua no DTO e sustenta a regra das ações em lote (só cópias em
    // edição) — apenas a coluna visual saiu. Com a regra "um código por vez" (Frente 8),
    // o Editavel deixou de ser distinção visível: é o par publicado×edição do legado.

    // Coluna: Ações
    cols.push({
      field: "acoes",
      headerName: "Ações",
      width: 70,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Visualizar detalhes" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onVisualizarDetalhes(params.row.id);
            }}
            sx={{ color: "primary.main" }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    });

    return cols;
  }, [onVisualizarDetalhes]);

  // Handler: Mudança de ordenação
  const handleSortModelChange = useCallback(
    (sortModel: any[]) => {
      if (sortModel.length === 0) {
        onOrdenacaoChange({ campo: null, direcao: null });
      } else {
        const [sort] = sortModel;
        onOrdenacaoChange({
          campo: sort.field as any,
          direcao: sort.sort === "asc" ? "asc" : "desc",
        });
      }
    },
    [onOrdenacaoChange]
  );

  // Verificar se há filtros ativos
  const temFiltrosAtivos = useMemo(() => {
    return !!(
      filtros.codigo ||
      filtros.descricao ||
      filtros.referencia ||
      filtros.linha ||
      (filtros.status && filtros.status.length > 0) ||
      (filtros.situacao && filtros.situacao.length > 0)
    );
  }, [filtros]);

  // Configuração das colunas do header customizado
  // FASE 05 — 6ª validação/PO 2026-08-03 (A2): Descrição ganha minWidth 200 (igual à coluna
  // real do DataGrid), senão o cabeçalho/filtro encolhem além da coluna de dados e desalinham.
  const headerConfig = useMemo<Array<{ field: string; label: string; width?: number; flex?: number; minWidth?: number; showFilter: boolean }>>(() => [
    { field: "imagem", label: "Imagem", width: 80, showFilter: false },
    { field: "codigo", label: "Código", width: 160, showFilter: true },
    { field: "descricao", label: "Descrição", flex: 1, minWidth: 200, showFilter: true },
    { field: "referencia", label: "Referência", width: 120, showFilter: true },
    { field: "linha", label: "Linha", width: 120, showFilter: true },
    { field: "status", label: "Status", width: 110, showFilter: true },
    { field: "situacao", label: "Situação", width: 120, showFilter: true },
    { field: "acoes", label: "Ações", width: 70, showFilter: false },
  ], []);

  return (
    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
      {/* Header Row - Títulos (fundo branco) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          minHeight: 45,
        }}
      >
        {headerConfig.map((col) => (
          <Box
            key={col.field}
            sx={{
              width: col.width || "auto",
              flex: col.flex || "none",
              minWidth: col.minWidth,
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: col.field === "imagem" || col.field === "acoes" ? "center" : "flex-start",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#333",
                fontSize: "0.8rem",
              }}
            >
              {col.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filter Row - Filtros (fundo cinza) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f0f0f0",
          borderBottom: "1px solid #e0e0e0",
          minHeight: 40,
        }}
      >
        {headerConfig.map((col) => (
          <Box
            key={`filter-${col.field}`}
            sx={{
              width: col.width || "auto",
              flex: col.flex || "none",
              minWidth: col.minWidth,
              // FASE 05 — 6ª validação/PO 2026-08-03 (A2): px igual ao da linha de título
              // (era 1, o título é 1.5) para o input do filtro alinhar com o cabeçalho.
              px: 1.5,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: col.field === "imagem" || col.field === "acoes" ? "center" : "flex-start",
            }}
          >
            {col.showFilter && (col.field === "status" || col.field === "situacao") ? (
              // FASE 05 — 2ª validação/PO 2026-07-24 (C3): multi-select por enum.
              <Select
                variant="standard"
                multiple
                fullWidth
                displayEmpty
                value={(filtros[col.field as "status" | "situacao"] as string[]) || []}
                onChange={(e) =>
                  handleFiltroMultiSelect(
                    col.field as "status" | "situacao",
                    typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[])
                  )
                }
                renderValue={(selected) => {
                  const sel = selected as string[];
                  if (sel.length === 0) {
                    return <span style={{ color: "#999", fontSize: "0.75rem" }}>FILTRO</span>;
                  }
                  const opcoes = opcoesPorCampo[col.field];
                  return sel
                    .map((v) => opcoes.find((o) => o.value === v)?.label || v)
                    .join(", ");
                }}
                sx={{
                  "&:before": { borderBottom: "none" },
                  "&:after": { borderBottom: "1px solid #3C90AD" },
                  "&:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                  "& .MuiSelect-select": {
                    fontSize: "0.75rem",
                    color: "#666",
                    py: 0,
                  },
                }}
              >
                {opcoesPorCampo[col.field].map((opcao) => (
                  <MenuItem key={opcao.value} value={opcao.value} sx={{ py: 0.25 }}>
                    <Checkbox
                      size="small"
                      checked={((filtros[col.field as "status" | "situacao"] as string[]) || []).indexOf(opcao.value) > -1}
                    />
                    <ListItemText primary={opcao.label} primaryTypographyProps={{ fontSize: "0.8rem" }} />
                  </MenuItem>
                ))}
              </Select>
            ) : col.showFilter ? (
              <TextField
                variant="standard"
                placeholder="FILTRO"
                size="small"
                fullWidth
                onChange={(e) => debouncedFiltroChange(col.field as keyof IFiltrosListagemN1, e.target.value)}
                sx={{
                  "& .MuiInput-underline:before": { borderBottom: "none" },
                  "& .MuiInput-underline:after": { borderBottom: "1px solid #3C90AD" },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                  "& .MuiInputBase-input": {
                    fontSize: "0.75rem",
                    color: "#666",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />
            ) : col.field === "acoes" && temFiltrosAtivos ? (
              // FASE 05 Ajustes — PO 2026-08-22 (item 4): o botão Limpar Filtros vivia como um
              // filho flex EXTRA no fim da linha de filtros (inexistente na linha de títulos) e
              // roubava ~38px da Descrição, desalinhando todas as colunas seguintes — e como o
              // filtro default status=Ativo conta como filtro ativo, o desalinhamento era o
              // estado normal da tela. Agora ele ocupa a célula de filtro de Ações (mesma
              // largura nas duas linhas), sem deslocar nada.
              <Tooltip title="Limpar todos os filtros">
                <IconButton onClick={handleLimparFiltros} size="small" color="primary">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        ))}
      </Box>

      {/* DataGrid sem header (header é customizado acima) */}
      <StripedDataGrid
        rows={produtos}
        columns={columns}
        /* FASE 05 — 6ª validação/PO (item 5): remove a 1ª coluna de checkbox do grid.
           O StripedDataGrid tem checkboxSelection=true por default; a seleção não é usada
           (as ações de fluxo agem por situação/tipo, não por seleção). */
        checkboxSelection={false}
        loading={isLoading}
        page={paginacao.page}
        pageSize={paginacao.limit}
        rowsPerPageOptions={[10, 25, 50, 100, 200]}
        paginationMode="server"
        rowCount={paginacao.total}
        hideFooter={false}
        headerHeight={0}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={handleSortModelChange}
        sortModel={
          ordenacao.campo
            ? [
              {
                field: ordenacao.campo,
                sort: ordenacao.direcao || "asc",
              },
            ]
            : []
        }
        getRowId={(row) => row.id}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            display: "none",
          },
          "& .MuiDataGrid-cell": {
            py: 1,
          },
          "& .MuiDataGrid-cell[data-field='imagem']": {
            backgroundColor: "inherit", // Herda a cor da linha
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(60, 144, 173, 0.05) !important",
          },
          "& .MuiDataGrid-row.odd": {
            backgroundColor: "#EBF4F9",
            "&:hover": {
              backgroundColor: "rgba(60, 144, 173, 0.1) !important",
            },
          },
          "& .MuiDataGrid-row.even": {
            backgroundColor: "#fff",
            "&:hover": {
              backgroundColor: "rgba(60, 144, 173, 0.05) !important",
            },
          },
        }}
      />
    </Box>
  );
};
